/**
 * 搜索推荐
 * 
 * @Author: Focci
 * @Date: 2023-09-29 14:48:09
 * @Last Modified by: Focci
 * @Last Modified time: 2023-09-29 14:48:09
 */

'use client'

import PropTypes from 'prop-types'
import useFetch from '@hook/useFetch'
import useProgressRouter from '@hook/useProgressRouter'
import useApp from '@hook/useApp'
import clsx from 'clsx'
import { ButtonBase, Skeleton } from '@comp/material'
import { useLocale, useTranslations } from 'next-intl'
import { map, pick, isFunction, isArray, some } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import { urls } from '@lib/parse'
import { getListingModeParam } from '@lib/listing-mode'
import SuggestionScoff from './SuggestionScoff'

function getChipData(data, locations) {
  return map(
    isArray(data?.suburbs) ? data.suburbs : [], 
    (item) => {
      const tmp = pick(item, ['id', 'type', 'name', 'slug'])
      const itemObj = locations?.[`${item.type}_${item.id}`]
      if(itemObj?.zh) {
        tmp.name = itemObj.zh
      }
      
      return tmp
    }
  )
}

function SuggestionSkeleton() {
  return (
    <div className="w-full">
      <Skeleton style={{ height: '1rem', width: '80%' }} />
      <Skeleton style={{ height: '1rem', width: '50%' }} />
      <Skeleton style={{ height: '1rem', width: '70%' }} />
    </div>
  )
}

function SuggestionChip({ name, ...buttonProps }) {
  return (
    <ButtonBase 
      className={clsx(['rounded-full', buttonProps.disabled && 'opacity-50'])} 
      {...buttonProps}
    >
      <span 
        className="
          text-f.7 bg-primary/10 md:bg-cf0 text-primary/90 md:text-c3b
          whitespace-nowrap rounded-full py-0.5 px-3 capitalize
        "
      >
        {name}
      </span>
    </ButtonBase>
  )
}

export default function SearchRecommend({
  isMapListing,
  disabled = [],
  type,
  onChange
}) {
  const locale = useLocale()
  const ts = useTranslations('search')
  const { isMobile } = useApp()
  const { routerPush } = useProgressRouter()
  const { data, isLoading } = useFetch('house-search-suggestion')
  const searchRecommend = useMemo(() => getChipData(data), [data])
  
  const handleClick = useCallback((chip) => {
    const param = chip.url 
      ? chip.data 
      : getListingModeParam({
        page: 1,
        [chip.type]: [{ label: chip.slug, value: chip.id }]
      }, isMobile).param

    const pathname = chip.url ? chip.url : urls.listing(type, param, locale, isMapListing)
    
    if(!isFunction(onChange)) {
      routerPush(pathname)
    } else {
      onChange({ pathname, param, chip, type: chip.type || type })
    }
  }, [onChange, isMobile, type, routerPush, locale, isMapListing])

  const checkDisabled = useCallback(
    (chip) => some(disabled, (s) => chip.type === s.type && s.value === chip.id), 
    [disabled]
  )

  return (
    <SuggestionScoff label={ts('searchRecommend')}>
      {isLoading && <SuggestionSkeleton />}
      {!isLoading && map(searchRecommend, (chip) => chip.id && (
        <SuggestionChip 
          key={chip.id} 
          {...chip}
          onClick={() => handleClick(chip)}
          disabled={checkDisabled(chip)}
        />
      ))}
    </SuggestionScoff>
  )
}

SearchRecommend.propTypes = {
  type: PropTypes.string,
  onChange: PropTypes.func,
}
